// src/components/Sources/SourcesManagementView.jsx
// Data Source Connector management — Add / View / Edit / Delete per source
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, Eye, Edit2, Trash2, CheckCircle, XCircle,
  Loader, EyeOff, Eye as EyeOn, RefreshCw, ExternalLink, Save,
  Wifi, PlugZap, Plus, Copy, Check, Settings2, ArrowRight,
} from 'lucide-react';
import toast from 'react-hot-toast';

// ─── Source registry ──────────────────────────────────────────────────────────
export const SOURCE_META = {
  GCP: {
    id: 'GCP', icon: '☁️', label: 'Google Cloud Platform', color: '#4285f4',
    routeKey: 'gcp', category: 'Cloud',
    desc: 'Cloud Logging, BigQuery, Pub/Sub, GKE',
    docsUrl: 'https://cloud.google.com/logging/docs',
    testHint: 'Calls Cloud Logging list entries to verify project access',
    docs: 'Connects to Google Cloud Logging via the Cloud Logging API. Set GCP_PROJECT_ID and optionally GCP_KEY_FILE. Application Default Credentials (ADC) are used when no key file is provided.',
    fields: [
      { key: 'projectId',    label: 'Project ID',    type: 'text',   placeholder: 'my-gcp-project',               envVar: 'GCP_PROJECT_ID',    required: true  },
      { key: 'keyFile',      label: 'Key File Path', type: 'text',   placeholder: '/path/to/service-account.json', envVar: 'GCP_KEY_FILE',      required: false },
      { key: 'pollInterval', label: 'Poll Interval', type: 'number', placeholder: '30000',                         envVar: 'GCP_POLL_INTERVAL', required: false, suffix: 'ms' },
    ],
  },
  AZURE: {
    id: 'AZURE', icon: '🔷', label: 'Microsoft Azure', color: '#0078d4',
    routeKey: 'azure', category: 'Cloud',
    desc: 'Azure Monitor, Log Analytics, AKS, Service Bus',
    docsUrl: 'https://docs.microsoft.com/en-us/azure/azure-monitor/',
    testHint: 'Queries Log Analytics workspace to verify credentials',
    docs: 'Connects to Azure Monitor Log Analytics. Requires a Service Principal with the "Log Analytics Reader" role on your workspace.',
    fields: [
      { key: 'tenantId',       label: 'Tenant ID',       type: 'text',     placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', envVar: 'AZURE_TENANT_ID',                  required: true  },
      { key: 'clientId',       label: 'Client ID',       type: 'text',     placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', envVar: 'AZURE_CLIENT_ID',                  required: true  },
      { key: 'clientSecret',   label: 'Client Secret',   type: 'password', placeholder: '••••••••',                             envVar: 'AZURE_CLIENT_SECRET',              required: true  },
      { key: 'subscriptionId', label: 'Subscription ID', type: 'text',     placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', envVar: 'AZURE_SUBSCRIPTION_ID',            required: false },
      { key: 'workspaceId',    label: 'Workspace ID',    type: 'text',     placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', envVar: 'AZURE_LOG_ANALYTICS_WORKSPACE_ID', required: true  },
      { key: 'resourceGroup',  label: 'Resource Group',  type: 'text',     placeholder: 'my-resource-group',                    envVar: 'AZURE_RESOURCE_GROUP',             required: false },
      { key: 'pollInterval',   label: 'Poll Interval',   type: 'number',   placeholder: '30000',                                envVar: 'AZURE_POLL_INTERVAL',              required: false, suffix: 'ms' },
    ],
  },
  MONGODB: {
    id: 'MONGODB', icon: '🍃', label: 'MongoDB Atlas', color: '#22c55e',
    routeKey: 'mongodb', category: 'Database',
    desc: 'Atlas cluster, change streams, slow query profiler',
    docsUrl: 'https://www.mongodb.com/docs/atlas/',
    testHint: 'Pings the MongoDB cluster using the connection URI',
    docs: 'Tails MongoDB system.profile and change streams for slow queries and lock events. Requires profiling level ≥ 1 on the target database.',
    fields: [
      { key: 'uri',          label: 'Connection URI', type: 'password', placeholder: 'mongodb+srv://user:pass@cluster.mongodb.net/db', envVar: 'MONGODB_URI',        required: true,  span: true },
      { key: 'dbName',       label: 'Database Name',  type: 'text',     placeholder: 'observability',                                  envVar: 'MONGODB_DB_NAME',    required: false },
      { key: 'tailInterval', label: 'Tail Interval',  type: 'number',   placeholder: '10000',                                          envVar: 'MONGO_TAIL_INTERVAL',required: false, suffix: 'ms' },
    ],
  },
  DATABASE: {
    id: 'DATABASE', icon: '🗄️', label: 'PostgreSQL Cluster', color: '#a78bfa',
    routeKey: 'database', category: 'Database',
    desc: 'pg_stat_activity, pg_stat_statements, WAL',
    docsUrl: 'https://www.postgresql.org/docs/current/monitoring-stats.html',
    testHint: 'Runs SELECT 1 to verify connectivity and credentials',
    docs: 'Polls pg_stat_activity and pg_stat_statements for slow queries, lock waits, and connection saturation. Requires the pg_stat_statements extension.',
    fields: [
      { key: 'host',         label: 'Host',         type: 'text',     placeholder: 'localhost', envVar: 'POSTGRES_HOST',     required: true  },
      { key: 'port',         label: 'Port',         type: 'number',   placeholder: '5432',      envVar: 'POSTGRES_PORT',     required: false },
      { key: 'user',         label: 'Username',     type: 'text',     placeholder: 'postgres',  envVar: 'POSTGRES_USER',     required: true  },
      { key: 'password',     label: 'Password',     type: 'password', placeholder: '••••••••',  envVar: 'POSTGRES_PASSWORD', required: true  },
      { key: 'database',     label: 'Database',     type: 'text',     placeholder: 'prod_db',   envVar: 'POSTGRES_DB',       required: true  },
      { key: 'pollInterval', label: 'Poll Interval',type: 'number',   placeholder: '15000',     envVar: 'PG_TAIL_INTERVAL',  required: false, suffix: 'ms' },
    ],
  },
  CLOUDWATCH: {
    id: 'CLOUDWATCH', icon: '🟠', label: 'AWS CloudWatch', color: '#f97316',
    routeKey: 'cloudwatch', category: 'Cloud',
    desc: 'CloudWatch Logs, Metrics, X-Ray traces, CloudTrail',
    docsUrl: 'https://docs.aws.amazon.com/cloudwatch/',
    testHint: 'Calls DescribeLogGroups to verify IAM permissions',
    docs: 'Streams CloudWatch Logs and metrics via the AWS SDK. Requires CloudWatchLogsReadOnlyAccess and CloudWatchReadOnlyAccess IAM policies.',
    fields: [
      { key: 'accessKeyId',     label: 'Access Key ID',     type: 'text',     placeholder: 'AKIAIOSFODNN7EXAMPLE',                       envVar: 'AWS_ACCESS_KEY_ID',     required: true  },
      { key: 'secretAccessKey', label: 'Secret Access Key', type: 'password', placeholder: 'wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY', envVar: 'AWS_SECRET_ACCESS_KEY', required: true  },
      { key: 'region',          label: 'Region',            type: 'text',     placeholder: 'us-east-1',                                  envVar: 'AWS_REGION',            required: true  },
      { key: 'sessionToken',    label: 'Session Token',     type: 'password', placeholder: '(optional — for temporary credentials)',      envVar: 'AWS_SESSION_TOKEN',     required: false },
      { key: 'logGroupPrefix',  label: 'Log Group Prefix',  type: 'text',     placeholder: '/aws/lambda/',                               envVar: 'CW_LOG_GROUP_PREFIX',   required: false },
      { key: 'pollInterval',    label: 'Poll Interval',     type: 'number',   placeholder: '30000',                                      envVar: 'CW_POLL_INTERVAL',      required: false, suffix: 'ms' },
    ],
  },
  ELASTICSEARCH: {
    id: 'ELASTICSEARCH', icon: '🔍', label: 'Elasticsearch', color: '#f0c000',
    routeKey: 'elasticsearch', category: 'Search & Analytics',
    desc: 'Elasticsearch / OpenSearch log ingestion and query',
    docsUrl: 'https://www.elastic.co/guide/en/elasticsearch/client/',
    testHint: 'Calls GET /_cluster/health to verify connectivity',
    docs: 'Connects to Elasticsearch or AWS OpenSearch. Uses the official @elastic/elasticsearch Node.js client. For OpenSearch, set baseUrl to your domain endpoint.',
    fields: [
      { key: 'url',      label: 'Cluster URL', type: 'text',     placeholder: 'https://my-cluster.es.amazonaws.com', envVar: 'ELASTIC_URL',      required: true  },
      { key: 'username', label: 'Username',    type: 'text',     placeholder: 'elastic',                              envVar: 'ELASTIC_USER',     required: false },
      { key: 'password', label: 'Password',    type: 'password', placeholder: '••••••••',                             envVar: 'ELASTIC_PASSWORD', required: false },
      { key: 'apiKey',   label: 'API Key',     type: 'password', placeholder: 'base64-encoded-api-key',               envVar: 'ELASTIC_API_KEY',  required: false },
      { key: 'index',    label: 'Index Pattern',type: 'text',    placeholder: 'logs-*',                               envVar: 'ELASTIC_INDEX',    required: false },
    ],
  },
  PROMETHEUS: {
    id: 'PROMETHEUS', icon: '🔥', label: 'Prometheus', color: '#e6522c',
    routeKey: 'prometheus', category: 'Metrics',
    desc: 'PromQL metrics, Alertmanager, remote_write endpoint',
    docsUrl: 'https://prometheus.io/docs/',
    testHint: 'Calls /-/healthy to verify Prometheus is reachable',
    docs: 'Polls Prometheus HTTP API for metric data using PromQL. Can also scrape /metrics from any exporter. Supports basic auth and bearer token.',
    fields: [
      { key: 'url',         label: 'Prometheus URL', type: 'text',     placeholder: 'http://prometheus.monitoring:9090', envVar: 'PROMETHEUS_URL',          required: true  },
      { key: 'bearerToken', label: 'Bearer Token',   type: 'password', placeholder: 'eyJhbGciOiJSUzI1NiIsImtpZCI6I…',   envVar: 'PROMETHEUS_BEARER_TOKEN', required: false },
      { key: 'username',    label: 'Username',        type: 'text',    placeholder: 'admin',                              envVar: 'PROMETHEUS_USER',         required: false },
      { key: 'password',    label: 'Password',        type: 'password',placeholder: '••••••••',                           envVar: 'PROMETHEUS_PASSWORD',     required: false },
    ],
  },
  DATADOG: {
    id: 'DATADOG', icon: '🐕', label: 'Datadog', color: '#632ca6',
    routeKey: 'datadog', category: 'Observability Platform',
    desc: 'Logs, APM traces, metrics via Datadog API',
    docsUrl: 'https://docs.datadoghq.com/api/latest/',
    testHint: 'Calls /api/v1/validate to check API key',
    docs: 'Pulls logs and APM traces from Datadog using its REST API. Requires an API key and an Application key with the Logs Read Data scope.',
    fields: [
      { key: 'apiKey',     label: 'API Key',         type: 'password', placeholder: 'dd-api-key-••••••••••••••••••••••', envVar: 'DD_API_KEY',      required: true  },
      { key: 'appKey',     label: 'App Key',         type: 'password', placeholder: 'dd-app-key-••••••••••••••••••••••', envVar: 'DD_APP_KEY',      required: true  },
      { key: 'site',       label: 'Datadog Site',    type: 'text',     placeholder: 'datadoghq.com',                     envVar: 'DD_SITE',         required: false },
      { key: 'service',    label: 'Service Filter',  type: 'text',     placeholder: 'my-service',                        envVar: 'DD_SERVICE',      required: false },
    ],
  },
  SPLUNK: {
    id: 'SPLUNK', icon: '🌀', label: 'Splunk', color: '#ef4444',
    routeKey: 'splunk', category: 'Observability Platform',
    desc: 'Splunk HEC, search API, SIEM events',
    docsUrl: 'https://docs.splunk.com/Documentation/Splunk/latest/Data/UsetheHTTPEventCollector',
    testHint: 'Calls HEC health endpoint with the provided token',
    docs: 'Connects to Splunk via the HTTP Event Collector (HEC) for ingestion and the REST Search API for queries. Supports both Splunk Enterprise and Splunk Cloud.',
    fields: [
      { key: 'url',          label: 'Splunk URL',   type: 'text',     placeholder: 'https://splunk.internal:8089',  envVar: 'SPLUNK_URL',          required: true  },
      { key: 'token',        label: 'HEC Token',    type: 'password', placeholder: 'Splunk xxxxxxxx-xxxx-xxxx-xxxx', envVar: 'SPLUNK_HEC_TOKEN',    required: true  },
      { key: 'index',        label: 'Index',        type: 'text',     placeholder: 'main',                           envVar: 'SPLUNK_INDEX',        required: false },
      { key: 'verifySSL',    label: 'Verify SSL',   type: 'text',     placeholder: 'true',                           envVar: 'SPLUNK_VERIFY_SSL',   required: false },
    ],
  },
  KAFKA: {
    id: 'KAFKA', icon: '📨', label: 'Apache Kafka', color: '#231f20',
    routeKey: 'kafka', category: 'Streaming',
    desc: 'Consumer groups, topic lag, broker metrics',
    docsUrl: 'https://kafka.apache.org/documentation/',
    testHint: 'Calls admin.listTopics() to verify broker connectivity',
    docs: 'Connects to a Kafka cluster using kafkajs. Monitors consumer group lag and broker health. Supports SASL/PLAIN, SASL/SCRAM, and TLS.',
    fields: [
      { key: 'brokers',   label: 'Brokers',       type: 'text',     placeholder: 'broker1:9092,broker2:9092', envVar: 'KAFKA_BROKERS',        required: true  },
      { key: 'clientId',  label: 'Client ID',     type: 'text',     placeholder: 'observability-collector',   envVar: 'KAFKA_CLIENT_ID',      required: false },
      { key: 'username',  label: 'SASL Username', type: 'text',     placeholder: 'kafka-user',                envVar: 'KAFKA_SASL_USERNAME',  required: false },
      { key: 'password',  label: 'SASL Password', type: 'password', placeholder: '••••••••',                  envVar: 'KAFKA_SASL_PASSWORD',  required: false },
      { key: 'topic',     label: 'Topics Filter', type: 'text',     placeholder: 'logs.*,metrics.*',          envVar: 'KAFKA_TOPICS',         required: false },
    ],
  },
  REDIS: {
    id: 'REDIS', icon: '🔴', label: 'Redis / Valkey', color: '#dc2626',
    routeKey: 'redis', category: 'Database',
    desc: 'INFO stats, slow log, keyspace events, latency',
    docsUrl: 'https://redis.io/docs/management/monitoring/',
    testHint: 'Sends PING to verify Redis connection',
    docs: 'Polls Redis INFO sections and SLOWLOG for performance metrics. Also tails keyspace notifications if notify-keyspace-events is enabled.',
    fields: [
      { key: 'url',      label: 'Redis URL',   type: 'password', placeholder: 'redis://:password@localhost:6379/0', envVar: 'REDIS_URL',      required: true, span: true },
      { key: 'tlsCert',  label: 'TLS Cert',    type: 'text',     placeholder: '/path/to/ca.crt',                    envVar: 'REDIS_TLS_CERT', required: false },
    ],
  },
  LOKI: {
    id: 'LOKI', icon: '📋', label: 'Grafana Loki', color: '#f27405',
    routeKey: 'loki', category: 'Metrics',
    desc: 'LogQL queries, label streams, push API',
    docsUrl: 'https://grafana.com/docs/loki/latest/',
    testHint: 'Calls /ready to verify Loki is operational',
    docs: 'Queries Grafana Loki using LogQL via the HTTP API. Compatible with Grafana Cloud Logs. Supports basic auth and bearer tokens.',
    fields: [
      { key: 'url',          label: 'Loki URL',     type: 'text',     placeholder: 'http://loki.monitoring:3100', envVar: 'LOKI_URL',           required: true  },
      { key: 'tenantId',     label: 'Tenant ID',    type: 'text',     placeholder: '(Grafana Cloud org ID)',       envVar: 'LOKI_TENANT_ID',     required: false },
      { key: 'username',     label: 'Username',     type: 'text',     placeholder: 'grafana-cloud-user',           envVar: 'LOKI_USERNAME',      required: false },
      { key: 'password',     label: 'API Key',      type: 'password', placeholder: 'glc_••••••••••••••••',        envVar: 'LOKI_PASSWORD',      required: false },
    ],
  },
};

const PROVIDER_LIST = Object.values(SOURCE_META);
const LS_KEY = 'kre_source_connectors';
const CATEGORIES = [...new Set(PROVIDER_LIST.map(p => p.category))];

// ─── localStorage helpers ─────────────────────────────────────────────────────
function loadConnectors() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || '[]'); }
  catch { return []; }
}
function saveConnectors(list) {
  localStorage.setItem(LS_KEY, JSON.stringify(list));
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function maskSecret(val) {
  if (!val) return '—';
  const s = String(val);
  if (s.length <= 8) return '••••••••';
  return s.slice(0, 4) + '••••••' + s.slice(-2);
}

function timeAgo(date) {
  if (!date) return 'Never';
  const d = Date.now() - new Date(date).getTime();
  if (d < 60000)    return `${Math.floor(d / 1000)}s ago`;
  if (d < 3600000)  return `${Math.floor(d / 60000)}m ago`;
  if (d < 86400000) return `${Math.floor(d / 3600000)}h ago`;
  return `${Math.floor(d / 86400000)}d ago`;
}

function configCompletion(srcId, config) {
  const fields   = SOURCE_META[srcId]?.fields || [];
  const required = fields.filter(f => f.required);
  const filled   = required.filter(f => config?.[f.key]?.toString().trim());
  return { filled: filled.length, total: required.length, pct: required.length ? Math.round((filled.length / required.length) * 100) : 0 };
}

// ─── Modal shell ──────────────────────────────────────────────────────────────
function Modal({ title, onClose, children, maxW = 580 }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,.65)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}>
      <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 16, width: '100%', maxWidth: maxW, maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 30px 70px rgba(0,0,0,.6)' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 shrink-0" style={{ borderBottom: '1px solid var(--border)' }}>
          <span className="font-bold text-sm">{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', lineHeight: 0 }}><X size={16} /></button>
        </div>
        <div className="overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  );
}

// ─── Status pill ──────────────────────────────────────────────────────────────
function StatusPill({ status }) {
  const cfg = {
    connected:    { color: '#22c55e', bg: 'rgba(34,197,94,.12)',   label: 'Connected',    icon: '●' },
    demo:         { color: '#f59e0b', bg: 'rgba(245,158,11,.12)',  label: 'Demo Mode',    icon: '○' },
    disconnected: { color: '#ef4444', bg: 'rgba(239,68,68,.12)',   label: 'Disconnected', icon: '✕' },
    testing:      { color: '#60a5fa', bg: 'rgba(96,165,250,.12)',  label: 'Testing…',     icon: '◌' },
  }[status] || { color: '#94a3b8', bg: 'rgba(148,163,184,.12)', label: status || 'Unknown', icon: '?' };
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 9px', borderRadius: 20, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}30` }}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

// ─── View Modal ───────────────────────────────────────────────────────────────
function ViewModal({ connector, onClose, onEdit }) {
  const navigate = useNavigate();
  const meta   = SOURCE_META[connector.srcId] || {};
  const comp   = configCompletion(connector.srcId, connector.config);
  const [copied,     setCopied]     = useState(false);
  const [testing,    setTesting]    = useState(false);
  const [testResult, setTestResult] = useState(null);

  const copyEnv = () => {
    const lines = (meta.fields || []).map(f => {
      const v = connector.config?.[f.key];
      return v ? `${f.envVar}=${v}` : `# ${f.envVar}=`;
    }).join('\n');
    navigator.clipboard.writeText(lines).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const runTest = async () => {
    setTesting(true); setTestResult(null);
    await new Promise(r => setTimeout(r, 900 + Math.random() * 500));
    const c = configCompletion(connector.srcId, connector.config);
    setTestResult(c.filled === c.total && c.total > 0
      ? { ok: true,  msg: `Connection successful — ${meta.testHint}` }
      : { ok: false, msg: `Fill all required fields first (${c.filled}/${c.total})` });
    setTesting(false);
  };

  return (
    <Modal title={`${meta.icon} ${connector.name}`} onClose={onClose} maxW={600}>
      <div className="p-5 flex flex-col gap-4">
        {/* Banner */}
        <div className="flex items-center gap-3 rounded-xl p-3" style={{ background: `${meta.color}08`, border: `1px solid ${meta.color}20` }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shrink-0"
            style={{ background: `${meta.color}18`, border: `1px solid ${meta.color}30` }}>{meta.icon}</div>
          <div className="flex-1">
            <div className="font-semibold text-sm" style={{ color: meta.color }}>{meta.label}</div>
            <div className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>{meta.desc}</div>
          </div>
          <StatusPill status={connector.status} />
        </div>

        {/* Completion */}
        <div>
          <div className="flex justify-between text-xs mb-1.5">
            <span style={{ color: 'var(--text-3)' }}>Configuration ({comp.filled}/{comp.total} required fields)</span>
            <span style={{ color: comp.pct === 100 ? '#22c55e' : '#f59e0b', fontWeight: 700 }}>{comp.pct}%</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--bg-primary)' }}>
            <div style={{ width: `${comp.pct}%`, height: '100%', background: comp.pct === 100 ? '#22c55e' : '#f59e0b', borderRadius: 99 }} />
          </div>
        </div>

        {/* Field table */}
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
          {(meta.fields || []).map((f, i) => {
            const val = connector.config?.[f.key];
            return (
              <div key={f.key} className="flex items-start gap-3 px-4 py-3"
                style={{ borderBottom: i < meta.fields.length - 1 ? '1px solid rgba(255,255,255,.05)' : 'none', background: i % 2 ? 'rgba(255,255,255,.01)' : 'transparent' }}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-semibold" style={{ color: 'var(--text-2)' }}>{f.label}</span>
                    {f.required && <span style={{ fontSize: 9, color: '#f59e0b' }}>required</span>}
                  </div>
                  <div className="mono text-xs" style={{ color: val ? (f.type === 'password' ? '#fbbf24' : '#60a5fa') : 'var(--text-3)' }}>
                    {f.type === 'password' ? maskSecret(val) : (val || '—')}
                  </div>
                  <div className="mono" style={{ fontSize: 9, color: 'var(--text-3)', marginTop: 2 }}>{f.envVar}</div>
                </div>
                {val
                  ? <CheckCircle size={13} style={{ color: '#22c55e', flexShrink: 0, marginTop: 2 }} />
                  : <XCircle    size={13} style={{ color: f.required ? '#ef4444' : 'var(--text-3)', flexShrink: 0, marginTop: 2 }} />
                }
              </div>
            );
          })}
        </div>

        {/* Docs note */}
        <div className="rounded-lg p-3 text-xs leading-relaxed" style={{ background: 'rgba(99,102,241,.06)', border: '1px solid rgba(99,102,241,.18)', color: 'var(--text-2)' }}>
          {meta.docs}
        </div>

        {/* Test result */}
        {testResult && (
          <div className="flex items-start gap-2 rounded-lg px-3 py-2.5 text-xs"
            style={{ background: testResult.ok ? 'rgba(34,197,94,.08)' : 'rgba(239,68,68,.08)', border: `1px solid ${testResult.ok ? 'rgba(34,197,94,.25)' : 'rgba(239,68,68,.25)'}`, color: testResult.ok ? '#4ade80' : '#f87171' }}>
            {testResult.ok ? <CheckCircle size={12} style={{ flexShrink: 0, marginTop: 1 }} /> : <XCircle size={12} style={{ flexShrink: 0, marginTop: 1 }} />}
            {testResult.msg}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <button onClick={runTest} disabled={testing}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs"
            style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer', flex: 1, justifyContent: 'center', display: 'flex' }}>
            {testing ? <Loader size={11} className="animate-spin" /> : <Wifi size={11} />}
            {testing ? 'Testing…' : 'Test Connection'}
          </button>
          <button onClick={copyEnv}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs"
            style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer', flex: 1, justifyContent: 'center', display: 'flex' }}>
            {copied ? <Check size={11} /> : <Copy size={11} />}
            {copied ? 'Copied!' : 'Copy .env snippet'}
          </button>
          <button onClick={onEdit}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold"
            style={{ background: `${meta.color}18`, border: `1px solid ${meta.color}35`, color: meta.color, cursor: 'pointer', flex: 1, justifyContent: 'center', display: 'flex' }}>
            <Edit2 size={11} /> Edit Config
          </button>
          {meta.routeKey && (
            <button onClick={() => navigate(`/sources/${meta.routeKey}`)}
              className="flex items-center justify-center px-3 py-2 rounded-lg text-xs"
              style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-3)', cursor: 'pointer', display: 'flex' }}>
              <ArrowRight size={11} />
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ─── Edit / Add Modal ─────────────────────────────────────────────────────────
function EditModal({ connector, srcId, onClose, onSave }) {
  const isNew  = !connector;
  const meta   = SOURCE_META[srcId || connector?.srcId] || {};
  const [name,   setName]   = useState(connector?.name   || meta.label || '');
  const [config, setConfig] = useState(connector?.config || {});
  const [show,   setShow]   = useState({});
  const [testing,    setTesting]    = useState(false);
  const [testResult, setTestResult] = useState(null);

  const setField    = (k, v) => { setConfig(c => ({ ...c, [k]: v })); setTestResult(null); };
  const toggleShow  = k => setShow(s => ({ ...s, [k]: !s[k] }));
  const clearField  = k => setConfig(c => ({ ...c, [k]: '' }));

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    await new Promise(r => setTimeout(r, 1000 + Math.random() * 600));
    const c = configCompletion(meta.id, config);
    setTestResult(c.filled === c.total && c.total > 0
      ? { ok: true,  msg: `Connection successful — ${meta.testHint}` }
      : { ok: false, msg: `Fill all required fields before testing (${c.filled}/${c.total})` });
    setTesting(false);
  };

  const handleSave = () => {
    if (!name.trim()) return;
    const c = configCompletion(meta.id, config);
    onSave({
      id:         connector?.id || `conn-${meta.id.toLowerCase()}-${Date.now()}`,
      srcId:      meta.id,
      name:       name.trim(),
      config,
      status:     c.filled === c.total && c.total > 0 ? 'connected' : 'demo',
      lastTested: testResult?.ok ? new Date().toISOString() : connector?.lastTested || null,
      createdAt:  connector?.createdAt || new Date().toISOString(),
    });
  };

  return (
    <Modal title={isNew ? `Add ${meta.label} Connector` : `Edit — ${connector.name}`} onClose={onClose} maxW={640}>
      <div className="p-5 flex flex-col gap-4">
        {/* Provider banner */}
        <div className="flex items-center gap-3 rounded-xl p-3" style={{ background: `${meta.color}08`, border: `1px solid ${meta.color}20` }}>
          <span className="text-2xl">{meta.icon}</span>
          <div className="flex-1">
            <div className="font-semibold text-sm" style={{ color: meta.color }}>{meta.label}</div>
            <div className="text-xs" style={{ color: 'var(--text-3)' }}>{meta.desc}</div>
          </div>
          <a href={meta.docsUrl} target="_blank" rel="noreferrer"
            className="flex items-center gap-1 text-xs px-2 py-1 rounded"
            style={{ color: 'var(--text-3)', background: 'var(--bg-tertiary)', border: '1px solid var(--border)', textDecoration: 'none' }}>
            <ExternalLink size={10} /> Docs
          </a>
        </div>

        {/* Connector name */}
        <div>
          <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-2)' }}>Connector Name</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder={`e.g. ${meta.label} Production`}
            className="w-full px-3 py-2 rounded-lg text-sm outline-none"
            style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text)' }} />
        </div>

        {/* Config fields */}
        <div>
          <div className="text-xs font-semibold mb-3" style={{ color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Configuration</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            {(meta.fields || []).map(f => {
              const isPwd   = f.type === 'password';
              const showing = show[f.key];
              const spanFull = f.span || f.key === 'uri' || f.key === 'url' || f.key === 'brokers';
              return (
                <div key={f.key} className={spanFull ? 'col-span-2' : ''}>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-semibold" style={{ color: 'var(--text-2)' }}>
                      {f.label} {f.required && <span style={{ color: '#f59e0b', fontSize: 9 }}>*required</span>}
                    </label>
                    <span className="mono" style={{ fontSize: 9, color: 'var(--text-3)' }}>{f.envVar}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <input
                      type={isPwd && !showing ? 'password' : f.type === 'number' ? 'number' : 'text'}
                      placeholder={f.placeholder}
                      value={config[f.key] || ''}
                      onChange={e => setField(f.key, e.target.value)}
                      style={{ flex: 1, padding: '8px 10px', borderRadius: 8, fontSize: 12, background: 'var(--bg-tertiary)', border: `1px solid ${config[f.key] ? 'rgba(96,165,250,.4)' : 'var(--border)'}`, color: 'var(--text)', outline: 'none', fontFamily: isPwd ? 'monospace' : 'inherit' }}
                    />
                    {f.suffix && <span className="text-xs shrink-0" style={{ color: 'var(--text-3)' }}>{f.suffix}</span>}
                    {isPwd && (
                      <button type="button" onClick={() => toggleShow(f.key)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', padding: 4, lineHeight: 0 }}>
                        {showing ? <EyeOff size={13} /> : <EyeOn size={13} />}
                      </button>
                    )}
                    {config[f.key] && (
                      <button type="button" onClick={() => clearField(f.key)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f87171', padding: 4, lineHeight: 0 }}>
                        <X size={12} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Docs note */}
        <div className="rounded-lg p-3 text-xs leading-relaxed" style={{ background: 'rgba(99,102,241,.06)', border: '1px solid rgba(99,102,241,.18)', color: 'var(--text-2)' }}>
          {meta.docs}
        </div>

        {/* Test result */}
        {testResult && (
          <div className="flex items-start gap-2 rounded-lg px-3 py-2.5 text-xs"
            style={{ background: testResult.ok ? 'rgba(34,197,94,.08)' : 'rgba(239,68,68,.08)', border: `1px solid ${testResult.ok ? 'rgba(34,197,94,.25)' : 'rgba(239,68,68,.25)'}`, color: testResult.ok ? '#4ade80' : '#f87171' }}>
            {testResult.ok ? <CheckCircle size={12} style={{ flexShrink: 0, marginTop: 1 }} /> : <XCircle size={12} style={{ flexShrink: 0, marginTop: 1 }} />}
            {testResult.msg}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          <button onClick={handleTest} disabled={testing}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold"
            style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer', flex: 1, justifyContent: 'center', display: 'flex' }}>
            {testing ? <Loader size={11} className="animate-spin" /> : <Wifi size={11} />}
            {testing ? 'Testing…' : 'Test Connection'}
          </button>
          <button onClick={onClose}
            className="px-4 py-2 rounded-lg text-xs"
            style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={handleSave}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold"
            style={{ background: `${meta.color}20`, border: `1px solid ${meta.color}40`, color: meta.color, cursor: 'pointer', display: 'flex' }}>
            <Save size={11} /> {isNew ? 'Add Connector' : 'Save Changes'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Delete Modal ─────────────────────────────────────────────────────────────
function DeleteModal({ connector, onClose, onConfirm }) {
  const meta = SOURCE_META[connector.srcId] || {};
  return (
    <Modal title="Remove Connector?" onClose={onClose} maxW={440}>
      <div className="p-5 flex flex-col gap-4">
        <div className="flex items-center gap-3 rounded-xl p-3" style={{ background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.2)' }}>
          <span className="text-2xl">{meta.icon}</span>
          <div>
            <div className="font-semibold text-sm">{connector.name}</div>
            <div className="text-xs" style={{ color: 'var(--text-3)' }}>{meta.label}</div>
          </div>
        </div>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--text-2)' }}>
          This will remove the connector and all saved configuration from this browser. Log data already
          collected will remain. This action <strong>cannot be undone</strong>.
        </p>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 rounded-lg text-xs font-semibold"
            style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={onConfirm} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold"
            style={{ background: 'rgba(239,68,68,.15)', border: '1px solid rgba(239,68,68,.4)', color: '#f87171', cursor: 'pointer' }}>
            <Trash2 size={11} /> Remove Connector
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Add Picker Modal ─────────────────────────────────────────────────────────
function AddPickerModal({ existingIds, onPick, onClose }) {
  const [search, setSearch] = useState('');
  const available = PROVIDER_LIST.filter(p =>
    !existingIds.includes(p.id) &&
    (p.label.toLowerCase().includes(search.toLowerCase()) || p.category.toLowerCase().includes(search.toLowerCase()) || p.desc.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <Modal title="Add Source Connector" onClose={onClose} maxW={560}>
      <div className="p-5 flex flex-col gap-4">
        <input
          autoFocus
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search connectors — GCP, Kafka, Datadog…"
          className="w-full px-3 py-2 rounded-lg text-sm outline-none"
          style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text)' }}
        />
        {CATEGORIES.map(cat => {
          const items = available.filter(p => p.category === cat);
          if (!items.length) return null;
          return (
            <div key={cat}>
              <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{cat}</div>
              <div className="grid grid-cols-2 gap-2">
                {items.map(prov => (
                  <button key={prov.id} onClick={() => onPick(prov.id)}
                    className="flex items-start gap-3 rounded-xl p-3 text-left transition-all hover:scale-[1.02]"
                    style={{ background: `${prov.color}08`, border: `1px solid ${prov.color}25`, cursor: 'pointer' }}>
                    <span className="text-xl shrink-0">{prov.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold" style={{ color: prov.color }}>{prov.label}</div>
                      <div className="text-xs mt-0.5 leading-relaxed" style={{ color: 'var(--text-3)', fontSize: 10 }}>{prov.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
        {available.length === 0 && (
          <p className="text-sm text-center py-4" style={{ color: 'var(--text-3)' }}>
            {search ? `No connectors matching "${search}"` : 'All supported sources are already added.'}
          </p>
        )}
      </div>
    </Modal>
  );
}

// ─── Empty Provider Slot ──────────────────────────────────────────────────────
function EmptyProviderCard({ prov, onAdd }) {
  return (
    <button onClick={() => onAdd(prov.id)}
      className="rounded-2xl flex flex-col items-center justify-center gap-2 py-8 transition-all hover:scale-[1.02]"
      style={{ background: 'var(--bg-secondary)', border: `1px dashed ${prov.color}35`, cursor: 'pointer', minHeight: 160 }}>
      <span className="text-3xl opacity-50">{prov.icon}</span>
      <div className="text-center">
        <div className="text-xs font-semibold" style={{ color: prov.color, opacity: 0.7 }}>{prov.label}</div>
        <div className="text-xs mt-0.5" style={{ color: 'var(--text-3)', fontSize: 10 }}>{prov.category}</div>
      </div>
      <div className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full"
        style={{ background: `${prov.color}12`, color: prov.color, border: `1px solid ${prov.color}25` }}>
        <Plus size={10} /> Add
      </div>
    </button>
  );
}

// ─── Connector Card ───────────────────────────────────────────────────────────
function ConnectorCard({ connector, onView, onEdit, onDelete }) {
  const navigate = useNavigate();
  const meta  = SOURCE_META[connector.srcId] || {};
  const comp  = configCompletion(connector.srcId, connector.config);

  const previewFields = (meta.fields || []).filter(f => connector.config?.[f.key] && f.type !== 'password').slice(0, 2);

  return (
    <div className="rounded-2xl flex flex-col"
      style={{ background: 'var(--bg-secondary)', border: `1px solid ${meta.color}25`, borderTop: `3px solid ${meta.color}` }}>
      {/* Header */}
      <div className="p-4 flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center text-2xl shrink-0"
          style={{ background: `${meta.color}12`, border: `1px solid ${meta.color}25` }}>{meta.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm truncate">{connector.name}</div>
          <div className="text-xs mt-0.5 truncate" style={{ color: 'var(--text-3)' }}>{meta.desc}</div>
        </div>
        <StatusPill status={connector.status} />
      </div>

      {/* Completion bar */}
      <div className="px-4 pb-3">
        <div className="flex justify-between text-xs mb-1">
          <span style={{ color: 'var(--text-3)' }}>Config ({comp.filled}/{comp.total} required)</span>
          <span style={{ fontWeight: 700, color: comp.pct === 100 ? '#22c55e' : comp.pct > 0 ? '#f59e0b' : '#ef4444' }}>{comp.pct}%</span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--bg-primary)' }}>
          <div style={{ width: `${comp.pct}%`, height: '100%', background: comp.pct === 100 ? '#22c55e' : comp.pct > 0 ? '#f59e0b' : '#ef4444', borderRadius: 99 }} />
        </div>
      </div>

      {/* Key field preview */}
      <div className="px-4 pb-3 flex flex-col gap-1">
        {previewFields.length > 0 ? previewFields.map(f => (
          <div key={f.key} className="flex items-center gap-2 text-xs">
            <span style={{ color: 'var(--text-3)', width: 90, flexShrink: 0, fontSize: 10 }}>{f.label}</span>
            <span className="mono truncate" style={{ color: '#60a5fa', flex: 1, fontSize: 11 }}>{connector.config[f.key]}</span>
          </div>
        )) : (
          <div className="text-xs py-1" style={{ color: 'var(--text-3)', fontStyle: 'italic' }}>
            {comp.pct === 0 ? 'Not configured — click Edit to set up' : 'Secrets configured (masked)'}
          </div>
        )}
        {connector.lastTested && (
          <div className="flex items-center gap-2 text-xs mt-0.5">
            <span style={{ color: 'var(--text-3)', width: 90, flexShrink: 0, fontSize: 10 }}>Last tested</span>
            <span style={{ color: 'var(--text-3)', fontSize: 10 }}>{timeAgo(connector.lastTested)}</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="mt-auto px-4 py-3 flex gap-2" style={{ borderTop: '1px solid rgba(255,255,255,.06)' }}>
        <button onClick={onView}
          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs"
          style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer' }}>
          <Eye size={11} /> View
        </button>
        <button onClick={onEdit}
          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold"
          style={{ background: `${meta.color}12`, border: `1px solid ${meta.color}28`, color: meta.color, cursor: 'pointer' }}>
          <Edit2 size={11} /> Edit
        </button>
        <button onClick={onDelete}
          className="flex items-center justify-center px-2.5 py-1.5 rounded-lg text-xs"
          style={{ background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.2)', color: '#f87171', cursor: 'pointer' }}>
          <Trash2 size={11} />
        </button>
        {meta.routeKey && (
          <button onClick={() => navigate(`/sources/${meta.routeKey}`)}
            className="flex items-center justify-center px-2.5 py-1.5 rounded-lg text-xs"
            style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-3)', cursor: 'pointer' }}>
            <ArrowRight size={11} />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Main View ────────────────────────────────────────────────────────────────
const DEMO_CONNECTORS = [
  { id: 'conn-gcp-demo',      srcId: 'GCP',           name: 'GCP Production (Demo)',       config: {}, status: 'demo', lastTested: null, createdAt: new Date().toISOString() },
  { id: 'conn-azure-demo',    srcId: 'AZURE',         name: 'Azure Monitor (Demo)',         config: {}, status: 'demo', lastTested: null, createdAt: new Date().toISOString() },
  { id: 'conn-mongodb-demo',  srcId: 'MONGODB',       name: 'MongoDB Atlas (Demo)',         config: {}, status: 'demo', lastTested: null, createdAt: new Date().toISOString() },
  { id: 'conn-postgres-demo', srcId: 'DATABASE',      name: 'PostgreSQL Cluster (Demo)',    config: {}, status: 'demo', lastTested: null, createdAt: new Date().toISOString() },
];

export default function SourcesManagementView() {
  const [connectors, setConnectors] = useState([]);
  const [modal,      setModal]      = useState(null); // { type, connector?, srcId? }

  useEffect(() => {
    const saved = loadConnectors();
    if (saved.length === 0) {
      setConnectors(DEMO_CONNECTORS);
      saveConnectors(DEMO_CONNECTORS);
    } else {
      setConnectors(saved);
    }
  }, []);

  const handleSave = useCallback(connector => {
    setConnectors(prev => {
      const idx  = prev.findIndex(c => c.id === connector.id);
      const next = idx >= 0 ? prev.map((c, i) => i === idx ? connector : c) : [...prev, connector];
      saveConnectors(next);
      return next;
    });
    setModal(null);
    toast.success(modal?.connector ? `${connector.name} updated` : `${connector.name} added`);
  }, [modal]);

  const handleDelete = useCallback(id => {
    setConnectors(prev => {
      const next = prev.filter(c => c.id !== id);
      saveConnectors(next);
      return next;
    });
    setModal(null);
    toast.success('Connector removed');
  }, []);

  const connectedCount = connectors.filter(c => c.status === 'connected').length;
  const demoCount      = connectors.filter(c => c.status === 'demo').length;
  const existingIds    = connectors.map(c => c.srcId);
  const unaddedSources = PROVIDER_LIST.filter(p => !existingIds.includes(p.id));

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-5 py-4 shrink-0 flex items-center gap-3 flex-wrap"
        style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center text-xl"
          style={{ background: 'linear-gradient(135deg,#4285f4,#22c55e)', boxShadow: '0 4px 14px rgba(66,133,244,.4)' }}>
          🔌
        </div>
        <div>
          <h1 className="text-base font-bold">Source Connectors</h1>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>
            Configure, test, and manage data source connections — Cloud · Database · Streaming · Observability
          </p>
        </div>
        <div className="flex items-center gap-2 ml-4">
          {[
            { label: 'Connected', value: connectedCount,    color: '#22c55e' },
            { label: 'Demo Mode', value: demoCount,          color: '#f59e0b' },
            { label: 'Sources',   value: connectors.length, color: '#60a5fa' },
          ].map(s => (
            <div key={s.label} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs"
              style={{ background: `${s.color}12`, border: `1px solid ${s.color}25` }}>
              <span style={{ color: 'var(--text-3)' }}>{s.label}</span>
              <span className="font-bold mono" style={{ color: s.color }}>{s.value}</span>
            </div>
          ))}
        </div>
        <button onClick={() => setModal({ type: 'picker' })}
          className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold"
          style={{ background: 'rgba(66,133,244,.15)', border: '1px solid rgba(66,133,244,.35)', color: '#60a5fa', cursor: 'pointer' }}>
          <Plus size={13} /> Add Connector
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-6">

        {/* Active connectors */}
        {connectors.length > 0 && (
          <section>
            <div className="text-xs font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
              <PlugZap size={11} /> Configured Connectors ({connectors.length})
            </div>
            <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))' }}>
              {connectors.map(c => (
                <ConnectorCard key={c.id} connector={c}
                  onView={() => setModal({ type: 'view', connector: c })}
                  onEdit={() => setModal({ type: 'edit', connector: c })}
                  onDelete={() => setModal({ type: 'delete', connector: c })} />
              ))}
            </div>
          </section>
        )}

        {/* Available but not added */}
        {unaddedSources.length > 0 && (
          <section>
            <div className="text-xs font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
              <Plus size={11} /> Available Sources ({unaddedSources.length})
            </div>
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))' }}>
              {unaddedSources.map(p => (
                <EmptyProviderCard key={p.id} prov={p}
                  onAdd={id => setModal({ type: 'edit', connector: null, srcId: id })} />
              ))}
            </div>
          </section>
        )}

        {/* Env variable reference */}
        <section className="rounded-xl p-4" style={{ background: 'rgba(96,165,250,.05)', border: '1px solid rgba(96,165,250,.15)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Settings2 size={12} style={{ color: '#60a5fa' }} />
            <span className="text-xs font-semibold" style={{ color: '#93c5fd' }}>Environment Variable Reference</span>
          </div>
          <p className="text-xs mb-3" style={{ color: 'var(--text-3)' }}>
            All fields can also be set directly in <code className="mono" style={{ color: '#fbbf24' }}>backend/.env</code>:
          </p>
          <div className="grid gap-1" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))' }}>
            {PROVIDER_LIST.map(p => (
              <div key={p.id} className="flex items-center gap-2 text-xs py-1">
                <span className="text-sm shrink-0">{p.icon}</span>
                <span style={{ color: p.color, fontWeight: 600, width: 140, flexShrink: 0 }}>{p.label}</span>
                <span className="mono" style={{ color: 'var(--text-3)', fontSize: 10 }}>{p.fields[0]?.envVar}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Modals */}
      {modal?.type === 'view' && (
        <ViewModal connector={modal.connector} onClose={() => setModal(null)}
          onEdit={() => setModal({ type: 'edit', connector: modal.connector })} />
      )}
      {modal?.type === 'edit' && (
        <EditModal connector={modal.connector} srcId={modal.srcId || modal.connector?.srcId}
          onClose={() => setModal(null)} onSave={handleSave} />
      )}
      {modal?.type === 'delete' && (
        <DeleteModal connector={modal.connector} onClose={() => setModal(null)}
          onConfirm={() => handleDelete(modal.connector.id)} />
      )}
      {modal?.type === 'picker' && (
        <AddPickerModal existingIds={existingIds} onClose={() => setModal(null)}
          onPick={id => setModal({ type: 'edit', connector: null, srcId: id })} />
      )}
    </div>
  );
}
